import React from "react";
import { useAsyncModal } from "../../components/asyncModal";
import { Form, Formik } from "formik";
import { array, boolean, object, string } from "yup";
import styled from "@emotion/styled";
import {
  Button,
  DialogActions,
  DialogContent,
  List,
  Typography,
} from "@mui/material";
import { DateTime } from "luxon";
import FormikListItemCheckbox from "../../formikComponents/formik-list-item-checkbox";
import { ContentCopy } from "@mui/icons-material";
import { useSetAtom } from "jotai";
import { customAlertStateAtom } from "../../components/custom-alert";

interface FirebaseAppDistributionResponse {
  success: boolean;
  projectId: string;
  apps: App[];
  summary: Summary;
}

interface App {
  appId: string;
  displayName: string;
  platform: "ANDROID" | "IOS";
  packageName: string | null;
  bundleId: string | null;
  releases: Release[];
  latestRelease?: Release;
  totalReleases: number;
}

interface Release {
  name: string;
  displayVersion: string;
  buildVersion: string;
  createTime: string;
  firebaseConsoleUri: string;
  testingUri: string;
  binaryDownloadUri: string;
  selected?: boolean; // Add this optional property
}

interface Summary {
  totalApps: number;
  androidApps: number;
  iosApps: number;
  totalReleases: number;
  appsWithReleases: number;
  appsWithoutReleases: number;
}

export type FirebaseModalData = FirebaseAppDistributionResponse;

interface AppReleaseToClone {
  appId: string;
  releaseId?: string; // Optional - if not provided, will clone latest release
  platform: string; // "ANDROID" or "IOS"
}

export interface FirebaseSubmitData {
  projectId: string;
  serviceAccountPath?: string;
  appsToClone: AppReleaseToClone[];
  concurrency?: number; // Optional - number of concurrent clones (default: 3)
}

export interface FirebaseSubmitResponse {
  success: boolean;
  message: string;
}

export const CloneBuildFromAppDistributionDialog = () => {
  const { data, submitModal, isSubmitting } = useAsyncModal<
    FirebaseModalData,
    FirebaseSubmitData,
    FirebaseSubmitResponse
  >();

  const initialValues = React.useMemo(() => {
    return {
      apps:
        data?.apps
          ?.filter((app) => app.releases.length > 0)
          ?.map((app) => ({
            ...app,
            releases: app.releases.map((release) => ({
              ...release,
              selected: false, // Initialize all releases as unselected
            })),
          })) ?? [],
    };
  }, [data]);

  const setCustomAlertState = useSetAtom(customAlertStateAtom);

  return (
    <Formik
      validationSchema={object().shape({
        apps: array()
          .of(
            object().shape({
              appId: string().defined(),
              displayName: string(),
              platform: string().defined(),
              packageName: string().nullable(),
              bundleId: string().nullable(),
              releases: array().of(
                object().shape({
                  name: string(),
                  displayVersion: string(),
                  buildVersion: string(),
                  createTime: string(),
                  firebaseConsoleUri: string(),
                  testingUri: string(),
                  binaryDownloadUri: string(),
                  selected: boolean().default(false),
                })
              ),
            })
          )
          .required("Required"),
      })}
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={async (values, formikHelpers) => {
        console.log("onSubmit called"); // Add this to track calls

        const selectedReleases = values.apps.flatMap((app) =>
          app.releases
            .filter((release) => release.selected)
            .map((release) => {
              const releaseId = release.name.split("/").pop();

              return {
                ...release,
                releaseId,
                appId: app.appId,
                platform: app.platform,
              };
            })
        );

        if (selectedReleases.length === 0) {
          formikHelpers.setFieldError(
            "apps",
            "Please select at least one app release"
          );
          return;
        }

        try {
          const firebaseSubmitData: FirebaseSubmitData = {
            projectId: data?.projectId ?? "",
            appsToClone: selectedReleases.map((release) => ({
              appId: release.appId,
              releaseId: release.releaseId,
              platform: release.platform,
            })),
            concurrency: 3,
          };
          await submitModal(firebaseSubmitData);
          console.log("Form submitted successfully");

          setCustomAlertState({
            open: true,
            type: "success",
            title: `Build cloned successfully`,
            description: `Build cloned successfully`,
          });
        } catch (error) {
          console.error("Submission error:", error);
          formikHelpers.setStatus("Submission failed");
          setCustomAlertState({
            open: true,
            type: "error",
            title: "Something went wrong",
            description: "An error occurred during cloning",
          });
        }
      }}
    >
      {({ values, isSubmitting: formikIsSubmitting, handleSubmit }) => {
        const selectedReleases = values.apps.flatMap((app) =>
          app.releases.filter((release) => release.selected)
        );
        return (
          <Form onSubmit={handleSubmit}>
            <DialogContent>
              {values.apps.map((app, appIndex: number) => (
                <AppSection key={app.appId}>
                  <Typography variant="subtitle2">{app.displayName}</Typography>
                  <List
                    sx={{
                      width: "100%",
                      bgcolor: "background.paper",
                    }}
                  >
                    {app.releases.map((release, releaseIndex) => (
                      <FormikListItemCheckbox
                        key={release.name}
                        disablePadding
                        name={`apps[${appIndex}].releases[${releaseIndex}].selected`}
                        primary={release.displayVersion}
                        secondary={DateTime.fromISO(
                          release.createTime
                        ).toHTTP()}
                        dense
                      />
                    ))}
                  </List>
                </AppSection>
              ))}
            </DialogContent>
            <DialogActions>
              <Button
                disabled={
                  selectedReleases.length === 0 ||
                  isSubmitting ||
                  formikIsSubmitting
                }
                loading={formikIsSubmitting}
                type="submit"
                startIcon={<ContentCopy />}
                variant="contained"
              >
                {formikIsSubmitting ? "Cloning..." : "Clone"}
              </Button>
            </DialogActions>
          </Form>
        );
      }}
    </Formik>
  );
};
const AppSection = styled.div`
  display: flex;
  flex-direction: column;
`;
